import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import DateRangeFilter, { rangeFor } from "@/components/DateRangeFilter";
import EffBadge from "@/components/EffBadge";
import { fmtNum } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BrainCircuit, AlertTriangle, TrendingUp, TrendingDown, Minus } from "lucide-react";

const TrendIcon = ({ t }) => t === "Yükseliyor" ? <TrendingUp size={14} className="text-emerald-400" />
  : t === "Düşüyor" ? <TrendingDown size={14} className="text-red-400" /> : <Minus size={14} className="text-muted-foreground" />;

export default function AiAnalysis() {
  const [range, setRange] = useState(() => rangeFor({ key: "1m", months: 1 }));
  const [bottleneck, setBottleneck] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get(`/analytics/bottleneck?start=${range.start}&end=${range.end}`)
      .then((r) => setBottleneck(r.data)).catch(() => {});
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setLoading(true); setAnalysis("");
    try {
      const r = await api.post("/ai/analysis", range, { timeout: 120000 });
      setAnalysis(r.data.analysis);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="ai-analysis-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">AI Yönetici Analizi</h1>
          <p className="text-sm text-muted-foreground mt-1">Darboğaz tespiti, trend yönü ve otomatik yönetici özeti</p>
        </div>
        <Button onClick={generate} disabled={loading} data-testid="ai-generate-button">
          <BrainCircuit size={16} className="mr-1.5" />
          {loading ? "Analiz oluşturuluyor..." : "Yönetici Analizi Oluştur"}
        </Button>
      </div>
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="ai-date" />

      {analysis && (
        <Card className="p-5 bg-card border-border" data-testid="ai-analysis-result">
          <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
            <BrainCircuit size={17} className="text-primary" /> Yönetici Özeti ({range.start} — {range.end})
          </h3>
          <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{analysis}</div>
        </Card>
      )}

      {bottleneck && (
        <>
          {bottleneck.findings.length > 0 && (
            <Card className="p-4 bg-amber-500/5 border-amber-500/30" data-testid="ai-findings-panel">
              <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm mb-2">
                <AlertTriangle size={16} /> Otomatik Tespitler
              </div>
              <ul className="space-y-1">
                {bottleneck.findings.map((f, i) => (
                  <li key={f} className="text-sm text-muted-foreground" data-testid={`ai-finding-${i}`}>• {f}</li>
                ))}
              </ul>
            </Card>
          )}
          <Card className="p-4 bg-card border-border" data-testid="ai-bottleneck-table">
            <h3 className="text-lg font-medium mb-3">Bölüm Verimlilik Sıralaması (düşükten yükseğe)</h3>
            <div className="space-y-2.5">
              {bottleneck.departments.map((d, i) => (
                <div key={d.code} className="flex items-center gap-3" data-testid={`ai-dept-${d.code}`}>
                  <span className="text-xs font-mono text-muted-foreground w-5">{i + 1}.</span>
                  <span className="text-sm w-32 truncate">{d.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full transition-[width] duration-500"
                      style={{ width: `${Math.min(100, d.efficiency || 0)}%`, backgroundColor: d.efficiency < 70 ? "#DC2626" : d.efficiency < 80 ? "#EA580C" : d.efficiency < 90 ? "#D97706" : d.efficiency < 100 ? "#2563EB" : "#16A34A" }} />
                  </div>
                  <EffBadge value={d.efficiency} testid={`ai-dept-eff-${d.code}`} />
                  <span className="flex items-center gap-1 text-xs text-muted-foreground w-24">
                    <TrendIcon t={d.trend} /> {d.trend}
                  </span>
                  <span className="text-xs font-mono text-muted-foreground w-24 text-right">{fmtNum(d.actual)} / {fmtNum(d.standard)}</span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
